import { useState } from "react";
import { AskDeviceToServer, AskServerToDevice } from "../core/requests";
import CodeBlock from "./CodeBlock";
import { useTranslation } from "react-i18next";

type ChatMode = "none" | "device-to-server" | "server-to-device";

type ChatMessage = {
    role: "user" | "assistant";
    blocks: {
        type: "text" | "code";
        value: string;
    }[];
};


function extractCode(content: string) {
    const codeRegex = /```(?:javascript|js)?([\s\S]*?)```/g;

    const blocks: { type: "code" | "text"; value: string }[] = [];
    let lastIndex = 0;
    let match;

    while ((match = codeRegex.exec(content)) !== null) {
        if (match.index > lastIndex) {
            blocks.push({
                type: "text",
                value: content.slice(lastIndex, match.index).trim()
            });
        }

        blocks.push({
            type: "code",
            value: match[1].trim()
        });

        lastIndex = codeRegex.lastIndex;
    }

    if (lastIndex < content.length) {
        blocks.push({
            type: "text",
            value: content.slice(lastIndex).trim()
        });
    }

    return blocks.filter(b => b.value);
}

function ChatConversation({
    mode,
    onBack
}: {
    mode: ChatMode;
    onBack: () => void;
}) {
    const { t } = useTranslation();
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);

    const sendMessage = async () => {
        if (!input.trim()) return;

        setMessages(m => [
            ...m,
            {
                role: "user",
                blocks: [{ type: "text", value: input }]
            }
        ]);

        setInput("");
        setLoading(true);

        try {
            const result =
                mode === "device-to-server"
                    ? await AskDeviceToServer(input)
                    : await AskServerToDevice(input);

            setMessages(m => [
                ...m,
                {
                    role: "assistant",
                    blocks: extractCode(result)
                }
            ]);
        } catch {
            setMessages(m => [
                ...m,
                {
                    role: "assistant",
                    blocks: [{ type: "text", value: t("AI.SERVER_ERROR") }]
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {/* Header */}
            <div className="ai-header">
                <button onClick={onBack}>←</button>
                <span>
                    {mode === "device-to-server"
                        ? t("AI.MODE.DEVICE_TO_SERVER_ANALYZER")
                        : t("AI.MODE.SERVER_TO_DEVICE_ANALYZER")}
                </span>
            </div>

            {/* Messages */}
            <div className="ai-messages">
                {messages.map((msg, i) => (
                    <div key={i} className={`ai-msg ${msg.role}`}>
                        {msg.blocks.map((b, j) =>
                            b.type === "code" ? (
                                <CodeBlock key={j} code={b.value} />
                            ) : (
                                <div key={j} className="text-msg">
                                    {b.value}
                                </div>
                            )
                        )}
                    </div>
                ))}
                {loading && <div className="ai-typing">{t("AI.PROCESSING")}</div>}
            </div>

            {/* Input */}
            <div className="ai-input">
                <textarea
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                        }
                    }}
                />
                <button onClick={sendMessage}>{t("LOCATION.SEND")}</button>
            </div>
        </>
    );
}

export { ChatConversation }
